import React, { ReactElement, useContext, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Badge } from "@chakra-ui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ProfileContext } from "../../../../store/ProfileProvider";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type Credit = {
  data: string;
  description: string;
};

type RowData = {
  id: string;
  created_at: string;
  record_owner: string;
  credit: Credit;
  asset: string;
  status: string;
};

const CommissionList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel
}) => {
    const { profileTimeZone } = useContext(ProfileContext);

    interface RecordStatus {
      status: string;
      value: string;
      display: ReactElement;
    }

    const statusClasses = "status";

    const statusList: RecordStatus[] = [
      {
        status: "pending",
        value: "pending",
        display: (
          <Badge className={`${statusClasses} status-pending`}>Pending</Badge>
        ),
      },
      {
        status: "complete",
        value: "complete",
        display: (
          <Badge className={`${statusClasses} status-successful`}>
            Completed
          </Badge>
        ),
      },
      {
        status: "cancelled",
        value: "cancelled",
        display: (
          <Badge className={`${statusClasses} status-cancelled`}>Cancelled</Badge>
        ),
      },
      {
        status: "failed",
        value: "failed",
        display: (
          <Badge className={`${statusClasses} status-failed`}>Failed</Badge>
        ),
      },
    ];

    const handleClickCopy = async (id: string) => {
      try {
        await navigator.clipboard.writeText(id);
      } catch (err) {
        console.error("Failed to copy ID: ", err);
      }
    };

    const formatDate = (timestamp: string) => {
      return dayjs(Number(timestamp))
        .tz(profileTimeZone)
        .format("DD MMM YY - hh:mm A");
    };

    const CustomLoadingOverlay = () => (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <CircularProgress size={40} />
      </div>
    );

    const CopyIdRenderer = (params: ICellRendererParams) => (
      <Tooltip title="Copy ID" arrow placement="top">
        <div>
          <IconButton
            id={"button-copy-id-" + params.data?.id}
            onClick={() => handleClickCopy(params.data.id)}
            size="small"
          >
            <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
          </IconButton>
        </div>
      </Tooltip>
    );

    const CreatedDateRenderer = (params: ICellRendererParams) => (
      <>{formatDate(params.data.created_at)}</>
    );

    const CreditRenderer = (params: ICellRendererParams) => (
      <div>
        <div>{params.data.credit.data ? params.data.credit.data : ""}</div>
        <div style={{ fontSize: "0.85rem", color: "gray" }}>
          {params.data.credit.description ? params.data.credit.description : ""}
        </div>
      </div>
    );

    const StatusRenderer = (params: ICellRendererParams) => {
      const status = params.data.status;
      const statusItem = statusList.find((item) => item.status === status);
      return <>{statusItem ? statusItem.display : null}</>;
    };

    const columns: ColDef[] = [
      {
        field: "id",
        headerName: "",
        width: 50,
        sortable: false,
        resizable: false,
        cellRenderer: CopyIdRenderer,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      },
      {
        field: "created_at",
        headerName: "Created Date",
        sortable: false,
        minWidth: 180,
        flex: 1,
        cellRenderer: CreatedDateRenderer,
      },
      {
        field: "record_owner",
        headerName: "",
        sortable: false,
        minWidth: 180,
        flex: 1,
      },
      {
        field: "credit",
        headerName: "Amount",
        sortable: false,
        minWidth: 320,
        flex: 1,
        cellRenderer: CreditRenderer,
        headerClass: "ag-right-aligned-header",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
      },
      {
        field: "asset",
        headerName: "Asset",
        sortable: false,
        width: 80,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      },
      {
        field: "status",
        headerName: "",
        sortable: false,
        width: 120,
        cellRenderer: StatusRenderer,
        cellClass: "ag-cell-status",
      },
    ];

    const defaultColDef = useMemo<ColDef>(
      () => ({
        resizable: true,
        suppressMenu: true,
      }),
      []
    );

    const hidePagination = page === 0 && rowCount < pageSize;

    return (
      <>
        <Box className="card w-full">
          <div style={{ width: "100%" }}>
            <AgGridReact
              theme={themeAlpine}
              rowData={isLoading ? [] : tableData}
              columnDefs={columns}
              defaultColDef={defaultColDef}
              getRowId={(params) => params.data.id}
              rowHeight={48}
              headerHeight={44}
              domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
              suppressPaginationPanel={true}
              loading={isLoading}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate="No data available"
            />
          </div>

          <AgGridPagination
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPaginationChange={setPaginationModel}
            hidden={hidePagination}
          />
        </Box>
      </>
    );
  };

export default CommissionList;
